import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Coins, Flame, Gift, Hammer, Lock, Sparkles, Trophy,
  AlertCircle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PostClaimHollerNudge from "@/components/PostClaimHollerNudge";

/**
 * Daily Stash — "Crack the Crate" daily-tap engagement game.
 *
 * Habit-forming loop:
 *   1. /stash/status tells the user if they can play.
 *   2. They tap "CRACK IT" — POST /stash/crack returns the server's outcome.
 *   3. We animate a chest shaking → bursting open → revealing the result.
 *   4. Coins, bonus cracks, real prizes, or "nothin' but dust" — all branded.
 *   5. Streak counter motivates daily return; day-N gets guaranteed prize.
 *
 * The animation is purely cosmetic — the OUTCOME is decided server-side
 * (anti-tamper). The frontend just plays a 1.6s reveal on top.
 */
export default function StashPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | shaking | revealing | done
  const [outcome, setOutcome] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/stash/status");
      setStatus(data);
    } catch {
      setStatus({ mode: "off", can_play: false, reason: "Couldn't reach the Stash." });
    }
  };
  useEffect(() => { reload(); }, []);

  const crack = async ({ spendCoins = false, force = false } = {}) => {
    if (!user) {
      toast.error("Sign in to play.");
      navigate("/login?next=/stash");
      return;
    }
    setBusy(true);
    setPhase("shaking");
    try {
      const params = {};
      if (spendCoins) params.spend_coins = true;
      if (force) params.force = true;
      const { data } = await api.post("/stash/crack", null, { params });
      // Hold the shake animation for ~1.4s for tension.
      await new Promise((r) => setTimeout(r, 1400));
      setOutcome(data);
      setPhase("revealing");
      // After confetti animation, settle.
      setTimeout(() => setPhase("done"), 800);
    } catch (e) {
      setPhase("idle");
      const msg = formatApiError(e.response?.data?.detail) || "Couldn't crack the Stash.";
      toast.error(msg);
    } finally {
      setBusy(false);
      reload();
    }
  };

  const reset = () => {
    setOutcome(null);
    setPhase("idle");
  };

  if (!status) {
    return (
      <div className="py-20 text-center">
        <Loader2 size={28} className="inline animate-spin text-[#D7A62A]" />
      </div>
    );
  }

  const sponsor = status.sponsor || {};
  const hasSponsor = !!sponsor.name;
  const sponsorList = Array.isArray(status.sponsors) ? status.sponsors : [];
  const hasMultipleSponsors = sponsorList.length > 1;

  return (
    <div className="py-10 space-y-8" data-testid="stash-page">
      {/* Admin-only banner reminding them this is hidden from regular users */}
      {status.is_admin && status.mode !== "live" && (
        <div
          className="max-w-2xl mx-auto loot-card-flat p-3 border-2"
          style={{ borderColor: "#D7A62A", background: "rgba(215,166,42,0.08)" }}
          data-testid="stash-admin-banner"
        >
          <div className="flex items-start gap-2 text-sm">
            <Lock size={16} className="text-[#D7A62A] shrink-0 mt-0.5" />
            <div>
              <span className="font-display uppercase tracking-widest" style={{ color: "#D7A62A" }}>Admin preview</span>
              {" — "}
              The Stash is currently <strong>{status.mode}</strong>. Regular hunters can't see this page or the nav links until you flip mode to <strong>live</strong>. Cooldowns are bypassed for you so you can test as much as you want.
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <header className="max-w-2xl mx-auto text-center">
        <div className="overline" style={{ color: "#D7A62A" }}>One crack a day</div>
        <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight mt-2 leading-[0.9]">
          The
          <span style={{ color: "#76B82A" }}> Daily</span>
          <br />
          Stash.
        </h1>
        <p className="mt-4 text-lg text-[#F5F2E8]/80 max-w-lg mx-auto">
          Tap to crack the crate. Win coins, prizes — or come up with dust.
          Either way, come back tomorrow. <span className="text-[#D7A62A]">Streaks pay off.</span>
        </p>
      </header>

      {/* HUD: streak / coins / sponsor */}
      <div className="max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-3">
        <HudTile
          icon={<Flame size={18} />}
          label="Streak"
          value={status.streak > 0 ? `${status.streak} day${status.streak === 1 ? "" : "s"}` : "—"}
          accent="#E53935"
          hint={status.streak_hint}
        />
        <HudTile
          icon={<Coins size={18} />}
          label="Loot Coins"
          value={String(status.loot_coins || 0)}
          accent="#D7A62A"
          hint={`${status.coins_to_extra_crack || 50} = extra crack`}
        />
        {hasSponsor ? (
          <HudTile
            icon={<Sparkles size={18} />}
            label="This week"
            value={sponsor.name}
            accent="#76B82A"
            hint={sponsor.blurb || "Sponsored Stash"}
            logo={sponsor.logo_url}
          />
        ) : (
          <HudTile
            icon={<Gift size={18} />}
            label="Day-7 bonus"
            value="🔥 Guaranteed"
            accent="#76B82A"
            hint={`Streak ${status.streak_target || 7} → real prize`}
          />
        )}
      </div>

      {/* Multi-sponsor strip — shown only if 2+ sponsors are configured. */}
      {hasMultipleSponsors && (
        <div
          className="max-w-2xl mx-auto loot-card-flat p-3"
          data-testid="stash-sponsor-strip"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 shrink-0">
              Sponsored by
            </div>
            <div className="flex items-center gap-3 flex-wrap flex-1">
              {sponsorList.map((sp, i) => (
                <div
                  key={`${sp.name}-${i}`}
                  className="flex items-center gap-2 text-xs"
                  data-testid={`stash-sponsor-strip-item-${i}`}
                  title={sp.blurb || sp.name}
                >
                  {sp.logo_url && (
                    <img
                      src={sp.logo_url}
                      alt={sp.name}
                      className="h-6 w-6 object-contain border border-[#F5F2E8]/15"
                    />
                  )}
                  <span className="font-display uppercase tracking-wider">{sp.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MAIN ACT — chest */}
      <section className="max-w-md mx-auto">
        <div className="stash-stage">
          <ChestVisual phase={phase} outcome={outcome} />
          {phase === "done" && outcome ? (
            <ResultPanel
              outcome={outcome}
              onReset={reset}
              onCrackAgain={() => crack({ force: status.is_admin && status.already_played_today })}
              isAdmin={status.is_admin}
            />
          ) : (
            <CrackPanel status={status} onCrack={crack} busy={busy} phase={phase} />
          )}
        </div>
      </section>

      {/* Holler nudge after a real prize */}
      {phase === "done" && outcome && ["prize", "jackpot"].includes(outcome.outcome) && (
        <div className="max-w-lg mx-auto space-y-3">
          {outcome.claim && (
            <div
              className="loot-card-flat p-4 border-2"
              style={{ borderColor: "#76B82A", background: "rgba(118,184,42,0.08)" }}
              data-testid="stash-claim-cta"
            >
              <div className="overline" style={{ color: "#76B82A" }}>your prize is reserved</div>
              <div className="font-display uppercase text-lg mt-1">Claim code: {outcome.claim.claim_code}</div>
              <p className="font-mono text-xs text-[#F5F2E8]/75 mt-1">
                Tell us how you'd like to receive your <strong>{outcome.prize?.label}</strong>. Unclaimed prizes expire in 30 days.
              </p>
              <button
                onClick={() => navigate("/my-prizes")}
                className="btn-loot w-full text-xs mt-2 inline-flex items-center justify-center gap-1"
                data-testid="stash-go-claim"
              >
                <Trophy size={14} /> Claim it now
              </button>
            </div>
          )}
          <PostClaimHollerNudge
            name={user?.username || ""}
            city={user?.city || ""}
            didWin
            context={`cracking the Daily Stash and winning ${outcome.prize?.label || "loot"}`}
          />
        </div>
      )}

      {/* How it works */}
      <div className="max-w-2xl mx-auto loot-card-flat p-5 space-y-2">
        <div className="overline">House rules</div>
        <ul className="text-sm text-[#F5F2E8]/80 space-y-1.5">
          <li>• One free crack every 24 hours.</li>
          <li>• Streaks reset if you skip a day. Day {status.streak_target || 7} = guaranteed prize.</li>
          <li>• Loot Coins stack — spend {status.coins_to_extra_crack || 50} for an extra crack today.</li>
          <li>• Real prizes are limited inventory — stocks update daily.</li>
          <li>• It's all in good fun. We pull the levers; the chest decides.</li>
        </ul>
        <div className="pt-2 border-t border-[#F5F2E8]/10 text-xs text-[#F5F2E8]/55 font-mono">
          <Lock size={10} className="inline mr-1" />
          Outcomes are decided on the server before the animation plays — no cheats, no mind games.
        </div>
      </div>
    </div>
  );
}

// =====================================================================
function HudTile({ icon, label, value, accent, hint, logo }) {
  return (
    <div className="loot-card-flat p-3">
      <div className="flex items-center gap-2 text-xs text-[#F5F2E8]/55">
        <span style={{ color: accent }}>{icon}</span>
        <span className="font-mono uppercase tracking-widest text-[10px]">{label}</span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        {logo && (
          <img src={logo} alt="" className="h-7 w-7 object-contain border border-[#F5F2E8]/15" />
        )}
        <div className="font-display uppercase text-lg leading-tight" style={{ color: accent }}>
          {value}
        </div>
      </div>
      {hint && (
        <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1 leading-snug">{hint}</div>
      )}
    </div>
  );
}

function ChestVisual({ phase, outcome }) {
  const burst = phase === "revealing" || phase === "done";
  return (
    <div className="stash-chest-wrap">
      {/* Burst rays */}
      {burst && <div className="stash-burst" aria-hidden />}
      {/* Chest body — pure CSS so we don't ship an image */}
      <div className={`stash-chest ${phase === "shaking" ? "stash-chest--shake" : ""} ${burst ? "stash-chest--open" : ""}`}>
        <div className="stash-chest-lid" />
        <div className="stash-chest-body">
          <div className="stash-chest-lock" />
          <div className="stash-chest-band" />
          {burst && outcome && (
            <div className="stash-chest-content">
              <OutcomeIcon outcome={outcome} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OutcomeIcon({ outcome }) {
  if (outcome.outcome === "prize" || outcome.outcome === "jackpot") {
    return outcome.prize?.image_url
      ? <img src={outcome.prize.image_url} alt="" className="h-20 w-20 object-contain" />
      : <Trophy size={56} className="text-[#76B82A]" />;
  }
  if (outcome.outcome === "coins") return <Coins size={56} className="text-[#D7A62A]" />;
  if (outcome.outcome === "bonus") return <Sparkles size={56} className="text-[#76B82A]" />;
  return <span className="text-3xl opacity-50">🪨</span>;
}

function CrackPanel({ status, onCrack, busy, phase }) {
  const isAdmin = status.is_admin;
  const lockedForUser = !status.can_play && !isAdmin;
  if (lockedForUser) {
    const isLocked = status.already_played_today;
    return (
      <div className="text-center mt-6 space-y-3">
        <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#F5F2E8]/55">
          {isLocked ? <Lock size={14} /> : <AlertCircle size={14} />}
          {isLocked ? "Locked til tomorrow" : "Closed"}
        </div>
        <p className="text-sm text-[#F5F2E8]/75 max-w-sm mx-auto">{status.reason}</p>
        {isLocked && (status.loot_coins || 0) >= (status.coins_to_extra_crack || 50) && (
          <button
            className="btn-loot text-xs inline-flex items-center gap-1"
            onClick={() => onCrack({ spendCoins: true })}
            data-testid="stash-spend-coins"
          >
            <Coins size={12} /> Spend {status.coins_to_extra_crack || 50} coins for extra crack
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="text-center mt-6 space-y-3">
      <button
        onClick={() => onCrack({ force: isAdmin && status.already_played_today })}
        disabled={busy || phase === "shaking"}
        className="btn-loot text-base font-display tracking-widest px-8 py-3 inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="stash-crack-btn"
      >
        {phase === "shaking" ? (
          <><Loader2 size={16} className="animate-spin" /> Cracking…</>
        ) : (
          <><Hammer size={16} /> Crack it</>
        )}
      </button>
      {isAdmin && status.already_played_today && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#D7A62A]">
          🔓 Admin bypass — unlimited cracks (no streak progress)
        </div>
      )}
      {status.streak_hint && (
        <div className="font-mono text-xs text-[#D7A62A]">{status.streak_hint}</div>
      )}
    </div>
  );
}

function ResultPanel({ outcome, onReset, onCrackAgain, isAdmin }) {
  const isPrize = outcome.outcome === "prize" || outcome.outcome === "jackpot";
  const isBonus = outcome.outcome === "bonus";
  const color =
    outcome.outcome === "jackpot" ? "#D7A62A"
    : isPrize ? "#76B82A"
    : outcome.outcome === "coins" ? "#D7A62A"
    : isBonus ? "#76B82A"
    : "#F5F2E8";
  return (
    <div className="text-center mt-5 space-y-3" data-testid="stash-result">
      <div
        className="font-display uppercase text-3xl md:text-4xl leading-tight"
        style={{ color }}
      >
        {outcome.message}
      </div>
      {outcome.prize?.label && (
        <div className="font-mono text-sm text-[#F5F2E8]/75">{outcome.prize.label}</div>
      )}
      <div className="flex items-center justify-center gap-2 flex-wrap pt-2">
        {isBonus ? (
          <button onClick={onCrackAgain} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="stash-bonus-again">
            <Hammer size={12} /> Crack again
          </button>
        ) : (
          <button onClick={onReset} className="btn-ghost text-xs">Close</button>
        )}
        {isAdmin && !isBonus && (
          <button
            onClick={onCrackAgain}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
            data-testid="stash-admin-recrack"
          >
            <Hammer size={12} /> 🔓 Admin re-crack
          </button>
        )}
      </div>
    </div>
  );
}
